// Date and money text used by every screen's fields (web/src/fields.ts). No browser needed, so it can be tested directly.
const pad = (n: number) => String(n).padStart(2, "0");
export const isoToDmy = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

export interface DateReading {
  /** yyyy-mm-dd; "" when the box is empty. */
  iso: string;
  /** The year was left out and taken as next year (future-looking fields only). */
  nextYear: boolean;
}
/**
 * Read a typed date, day first. Accepts dd/mm/yyyy, dd/mm/yy and dd/mm (this year), with "/", "-", "." or a space
 * between the parts, or just the digits: ddmm (2409), ddmmyy (240926) or ddmmyyyy (24092026); also yyyy-mm-dd.
 * Two-digit years are 20yy. With `future` (ETC, ETD, ETA, due dates), a date typed without a year that would be more
 * than 3 months in the past is taken as next year. Returns null when it isn't a real date.
 */
export function readDate(text: string, o: { future?: boolean; today?: Date } = {}): DateReading | null {
  const t = String(text || "").trim();
  if (!t) return { iso: "", nextYear: false };
  const today = o.today || new Date();
  let d: number, m: number, y: number | null = null;
  let r = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (r) { y = +r[1]; m = +r[2]; d = +r[3]; }
  else if ((r = /^(\d{1,2})[/.\-\s]+(\d{1,2})(?:[/.\-\s]+(\d{2}|\d{4}))?$/.exec(t))) { d = +r[1]; m = +r[2]; y = r[3] ? +r[3] : null; }
  else if ((r = /^(\d{2})(\d{2})(\d{2}|\d{4})?$/.exec(t))) { d = +r[1]; m = +r[2]; y = r[3] ? +r[3] : null; }
  else return null;
  const noYear = y === null;
  if (y === null) y = today.getFullYear();
  else if (y < 100) y += 2000;
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1 || d > new Date(Date.UTC(y, m, 0)).getUTCDate()) return null;
  let nextYear = false;
  if (noYear && o.future) {
    const limit = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    if (new Date(y, m - 1, d) < limit && d <= new Date(Date.UTC(y + 1, m, 0)).getUTCDate()) { y += 1; nextYear = true; }
  }
  return { iso: `${y}-${pad(m)}-${pad(d)}`, nextYear };
}
/** The same as readDate, giving just yyyy-mm-dd ("" for empty, null if not a real date). */
export function parseDmy(text: string, o: { future?: boolean; today?: Date } = {}): string | null {
  return readDate(text, o)?.iso ?? null;
}

/** 1234.5 → "1,234.50" (2 decimals; up to 4 are kept for small per-unit costs such as 0.0045); "" stays "". */
export function formatMoney(v: number | string, maxDecimals = 4): string {
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, "").trim());
  if (String(v).trim() === "" || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, maxDecimals) });
}
